import { llmEn } from './llm/index.js'
import { ugcEn } from './ugc.js'

export const en = {
  visualGameAdmin: {
    app: {
      name: 'Visual Game Admin',
      waitlist: 'Waiting List',
      invitationCodes: 'Invite codes',
      users: 'Users',
      analytics: 'Overview',
      contentAnalytics: 'Content analytics',
      ga4Behavior: 'User behavior',
      conversionFunnel: 'Conversion funnel',
      generationRecords: 'Generation records',
      ugcReview: 'UGC review',
      tagManagement: 'Tag management',
      gameReleases: 'Game releases',
      homepageCarousel: 'Homepage carousel',
      assetLibrary: 'Asset library',
      promptLibrary: 'Prompt library',
      entityLibrary: 'Entity library',
      modelCapabilities: 'Model capabilities',
      h3Config: 'H3 video defaults',
      llm: 'Providers',
      llmModes: 'Scene routing',
      llmRuntime: 'Credential runtime',
      llmRecon: 'Reconciliation',
      llmCost: 'Cost',
      llmCalls: 'Call log',
      llmConversations: 'Agent conversations',
      llmTurnTimelines: 'Turn timelines',
      groups: {
        growth: 'Users and growth',
        billing: 'Commerce',
        analytics: 'Analytics',
        content: 'Content and assets',
        intentVideos: 'Intent videos',
        modelConfig: 'Model configuration',
        modelObservability: 'Usage observability',
        modelFinance: 'Cost and finance',
      },
      logout: 'Log out',
      theme: { toLight: 'Switch to light', toDark: 'Switch to dark' },
      forbidden: 'This account cannot access visual-game operations.',
    },
    llm: llmEn,
    ugc: ugcEn,
    homepageCarousel: {
      title: 'Homepage carousel', subtitle: 'Manage content, visibility, and order for the player homepage carousel.', add: 'Add item', empty: 'No carousel items', emptyDescription: 'Add a YouTube video or story to show it on the player homepage.', order: 'Order', type: 'Type', content: 'Content', status: 'Status', enabled: 'Enabled', disabled: 'Disabled', enable: 'Enable', disable: 'Disable', saved: 'Carousel item saved', deleted: 'Carousel item deleted', deleteConfirm: 'Delete this carousel item?', createTitle: 'Add carousel item', editTitle: 'Edit carousel item', referenceId: 'YouTube video ID', youtubeHint: 'Enter the ID after v= in the YouTube URL.', story: 'Choose story', storyHint: 'Episodes in the same series are combined into one carousel option.', storyPlaceholder: 'Select a story', storySearchPlaceholder: 'Search by story, series, or ID', storySearchEmpty: 'No matching stories', eyebrow: 'Eyebrow', itemTitle: 'Title override', summary: 'Summary override', types: { youtube: 'YouTube', story: 'Story' }, defaultStoryTitles: { otomeCastle: 'Dreams Woven Through Time' },
    },
    auth: {
      title: 'Operator sign in',
      subtitle: 'Manage visual-game early access requests.',
      email: 'Email',
      password: 'Password',
      login: 'Sign in',
      loggingIn: 'Signing in',
      failed: 'Sign in failed. Check the account and password.',
    },
    scenarioPicker: {
      title: 'Choose game',
      searchPlaceholder: 'Search by game, series, or ID',
      emptyResult: 'No matching games',
    },
    gameReleases: {
      title: 'Release games',
      subtitle: 'Promote the current published Stage version to Production and activate it immediately.',
      selectLabel: 'Release source',
      selectTitle: 'Choose a Stage game',
      selectHint: 'The selector is shared with Asset Library, so game names, covers, series, and IDs stay consistent.',
      emptyTitle: 'Choose a game to continue',
      emptyDescription: 'Only the selected game release is changed.',
      stage: 'Stage',
      production: 'Production',
      source: 'Source environment',
      target: 'Target environment',
      published: 'Published',
      version: 'Version',
      versionId: 'Version ID',
      publishedAt: 'Published at',
      assets: 'Version assets',
      syncAssets: 'Deduplicated objects',
      contentGraph: 'Entities / bindings / prompts',
      videoResources: 'Video resource library',
      intentVideos: '{count} intent videos',
      translations: 'Translations',
      checks: 'Publish checks',
      connection: { reachable: 'Connected', not_configured: 'Not configured', unreachable: 'Unavailable' },
      connectionHelp: {
        not_configured: 'Set SCENARIO_RELEASE_TARGET_URL and SCENARIO_RELEASE_TOKEN on Stage.',
        unreachable: 'Check the Production API and shared release token.',
      },
      readyTitle: 'Ready to release',
      resyncTitle: 'Production already runs this version',
      scope: 'Syncs the current scenario version, translations, entity resource graph, video library including intent-video vectors, and deduplicated media objects. Production users, runs, likes, and counters are not modified.',
      adminRequired: 'Operator accounts can inspect releases; an admin account is required to publish.',
      publish: 'Release to Production',
      resync: 'Resync release',
      successTitle: 'Production release is active',
      successSummary: 'v{version} activated · {copied} uploaded · {reused} reused · {size} transferred',
      confirmTitle: 'Confirm Production release',
      confirmScope: '{assets} deduplicated objects will be checked. Objects already present with the same hash are reused; only missing objects are downloaded and uploaded before the version switch.',
      noUserData: 'No user, run, progress, like, or counter data is copied.',
      confirmPublish: 'Confirm and release',
      historyTitle: 'Release history and logs',
      historyHint: 'Tracks the locked version, operator, asset transfer result, and every status transition.',
      historyEmpty: 'This game has not been released to Production yet.',
      operator: 'Operator',
      startedAt: 'Started at',
      transfer: 'Uploaded / reused / bytes',
      releaseStatus: {
        prepared: 'Prepared',
        transferring: 'Transferring',
        applying: 'Applying',
        succeeded: 'Succeeded',
        failed: 'Failed',
      },
    },
    tagManagement: {
      subtitle: '{count} packs · published, private and unlisted',
      placeholder: 'Drama, Mystery, Fantasy',
      searchLabel: 'Search tags',
      searchPlaceholder: 'Search tags or related games',
      stats: { tags: 'Tags', packs: 'Games', untagged: 'Untagged games' },
      table: { tag: 'Tag', usage: 'Usage', packs: 'Related games' },
      usageCount: '{count} games',
      viewPacks: 'View games',
      detailLabel: 'Tag details',
      editPack: 'Edit tags',
      editTitle: 'Edit tags for {title}',
      tagsLabel: 'Game tags',
      tagsHint: 'Separate tags with commas. Up to 12 tags.',
      emptyTitle: 'No matching tags',
      emptyDescription: 'Try another keyword or add tags to a game first.',
      tooManyTags: 'Each game can have up to 12 tags.',
      saved: 'Tags updated.',
    },
    common: {
      actions: 'Actions',
      cancel: 'Cancel',
      confirm: 'Confirm',
      retry: 'Retry',
      refresh: 'Refresh',
      reset: 'Reset',
      loading: 'Loading',
      empty: 'No entries match the current filters.',
      error: 'Request failed.',
      export: 'Export',
      view: 'View',
      invite: 'Invite',
      resend: 'Resend',
      reject: 'Reject',
      note: 'Note',
      create: 'Create',
      close: 'Close',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      working: 'Working…',
      back: 'Back',
    },
    billing: {
      readOnly: 'Read only: only admins can edit',
      saved: 'Saved.',
      turnsCovered: '≈ {n} turns',
      actionRefund: '{name} refund',
      ledgerReasons: {
        signup_gift: 'Signup gift', purchase: 'Points purchase', purchase_paid: 'Points purchase', purchase_bonus: 'Purchase bonus',
        daily_claim: 'Daily claim', admin_adjustment: 'Manual adjustment', manual_admin_grant: 'Manual grant', platform_seed: 'Starting points',
        model_usage: 'Usage billing', llm: 'Model call (legacy)', visual_game_tts: 'Speech synthesis (legacy)',
        payment_refund_reversal: 'Order refund reversal', payment_dispute_reversal: 'Chargeback reversal',
        unlock_episode: 'Episode unlock', unlock_branch: 'Branch unlock', unlock_continue: 'Continue unlock', unlock_ending: 'Ending unlock',
      },
      ledgerSources: {
        admin: 'Admin action', legacy_admin: 'Admin action (legacy)', daily_reward: 'Daily claim', visual_game_signup: 'Signup',
        platform: 'Platform', stripe: 'Stripe payment', system: 'System', gameplay: 'Gameplay', unlock: 'Content unlock',
        llm: 'Model call', tts: 'Speech synthesis', image_generation: 'Image generation', video_generation: 'Video generation',
      },
      ledgerRefTypes: {
        admin_action: 'Admin action', daily_claim: 'Daily claim', order: 'Order', run_turn: 'Turn', run_video: 'Full-run video',
        intent_video_request: 'Turn video task', unlock: 'Content unlock', source: 'Source',
        llm_chat: 'Model chat', llm_operation: 'Model call', tts_operation: 'Speech synthesis', visual_game_tts: 'Speech synthesis (legacy)',
        image_operation: 'Image generation', video_operation: 'Video generation',
      },
      modelUsageKinds: { llm: 'LLM', tts: 'Speech', image: 'Image', video: 'Video' },
      actionNames: { daily_claim: 'Daily claim', turn_play: 'Effective turn', full_run_video: 'Full-run video', intent_video_on_demand: 'Turn video', signup_reward: 'Signup reward' },
      actionHints: {
        daily_claim: 'Points a signed-in user can claim once per day from the top-up screen.',
        turn_play: 'Points reserved for every submitted turn; refunded automatically if generation fails.',
        full_run_video: 'Points charged each time a user confirms generating a full-run video.',
        intent_video_on_demand: 'Points charged each time a user confirms generating a single turn video. Refunded automatically if generation fails.',
        signup_reward: 'Points granted automatically when a new account registers.',
      },
      periods: { daily: 'Once per day', none: 'Per action' },
      enabledState: { on: 'Enabled', off: 'Disabled' },
      pager: { prev: 'Previous', next: 'Next', pageSize: 'Per page', summary: 'Page {page} · {n} rows' },
      dashboard: {
        nav: 'Overview',
        title: 'Commerce dashboard',
        windowDays: '{n} days',
        drill: 'Click to drill down',
        range: { label: 'Range', days: 'Last {n} days', custom: 'Custom', since: 'From', until: 'To' },
        sections: { revenue: 'Revenue', points: 'Points', conversion: 'Conversion', risk: 'Risk', products: 'Pack sales', spendMix: 'Spend mix' },
        charts: { revenue: 'Revenue', paidOrders: 'Paid orders', refundOrders: 'Refunds and disputes', pointsGranted: 'Points granted', pointsSpent: 'Points spent' },
        revenue: { gross: 'Gross revenue', refund: 'Refunded', net: 'Net revenue', paidOrders: 'Paid orders', payingUsers: 'Paying users', firstTimeBuyers: 'First-time buyers' },
        points: {
          purchasedPaid: 'Purchased (paid)', purchasedBonus: 'Purchased (bonus)', dailyClaim: 'Daily claims', signupReward: 'Signup rewards', adminGrant: 'Admin grants',
          actionSpent: '{name} spend', otherSpent: 'Other spend', actionRefunded: '{name} refunded points',
        },
        conversion: {
          newUsers: 'New signups', newUsersPaid: 'Signups that paid', signupToPaidRate: 'Signup to paid', activeUsers: 'Active users', claimUsers: 'Claimed daily points', claimRate: 'Claim rate',
          note: 'Signup to paid: users who registered in this window and have ever paid. Claim rate: users who claimed daily points over users with any ledger activity in the window.',
        },
        risk: {
          refundedOrders: 'Refunded orders', refundRate: 'Refund rate', disputedOrders: 'Disputed orders', disputeRate: 'Dispute rate',
          actionRefunds: '{name} failure refunds', shortfallOrders: 'Shortfall orders', shortfallPoints: 'Shortfall points',
          note: 'Refund and dispute rates divide reversals recorded in this window by orders paid in it. Shortfall: reversals that could not claw back every point because the balance was already spent.',
        },
        emptyProducts: 'No paid orders in this window.',
        emptySpend: 'No points spent in this window.',
      },
      policies: {
        title: 'Charging rules',
        description: 'How many points each action charges or grants. Changes apply to every user immediately.',
        table: { action: 'Action', points: 'Points', period: 'Frequency', timezone: 'Time zone', enabled: 'Status' },
        form: {
          title: 'Edit rule · {action}',
          points: 'Points',
          timezone: 'Time zone',
          timezoneHint: 'IANA name such as America/Los_Angeles. Daily claims reset at midnight in this zone.',
          enabled: 'Status',
          enabledHint: 'Disabled rules stop charging or granting without deleting the setting.',
        },
        errors: { points: 'Points must be a whole number of at least 1.', timezone: 'Daily actions need a time zone.' },
      },
      products: {
        title: 'Point packs',
        description: 'Packs sold through Stripe Checkout. Credited points include the bonus; the turn estimate uses the current effective-turn price.',
        table: { title: 'Pack', id: 'ID', paidPoints: 'Paid points', bonusPoints: 'Bonus points', totalPoints: 'Credited', turns: 'Turns', price: 'Price', stripePrice: 'Stripe price', active: 'Status' },
        activeState: { on: 'On sale', off: 'Off sale' },
        form: {
          title: 'Edit pack · {name}',
          name: 'Pack name',
          paidPoints: 'Paid points',
          bonusPoints: 'Bonus points',
          price: 'Price (USD)',
          priceHint: 'Up to two decimals, for example 9.99.',
          stripePrice: 'Stripe price ID',
          stripePriceHint: 'Leave empty until the price exists in Stripe. Checkout cannot start without it.',
          active: 'Status',
          totalPoints: 'Credited points',
          turns: 'Turns covered',
        },
        errors: {
          name: 'Pack name is required.',
          paidPoints: 'Paid points must be a whole number of at least 1.',
          bonusPoints: 'Bonus points must be a whole number of at least 0.',
          price: 'Price must be a positive amount with at most two decimals.',
        },
      },
      orders: {
        title: 'Payment orders',
        filters: { orderId: 'Order ID', userId: 'User ID', product: 'Pack', allProducts: 'All packs', status: 'Status', allStatuses: 'All statuses', scope: 'Reversal', scopeAll: 'All orders', scopeShortfall: 'Shortfall only', since: 'Created from', until: 'Created to' },
        table: { time: 'Created', id: 'Order ID', user: 'User', product: 'Pack', status: 'Status', amount: 'Amount', points: 'Credited', reversed: 'Reversed', shortfall: 'Shortfall' },
        status: { pending: 'Pending', paid: 'Paid', canceled: 'Canceled', failed: 'Failed', refunded: 'Refunded', disputed: 'Disputed' },
        empty: 'No orders match these filters.',
      },
      ledger: {
        title: 'Point ledger',
        filters: { userId: 'User ID', reason: 'Reason', source: 'Source', refType: 'Reference type', allReasons: 'All reasons', allSources: 'All sources', allRefTypes: 'All types', refId: 'Reference ID', since: 'From', until: 'To' },
        table: { time: 'Time', user: 'User', type: 'Type', amount: 'Change', balance: 'Balance after', reason: 'Reason', source: 'Source', reference: 'Reference', note: 'Note' },
        type: { grant: 'Grant', deduct: 'Deduct' },
        empty: 'No ledger entries match these filters.',
      },
    },
    invitationCodes: {
      title: 'Invite codes',
      createTitle: 'Create invite code',
      email: 'Player email',
      emailOptional: 'Optional; leave blank for an unbound code',
      count: 'Quantity',
      batchUnboundHint: 'Batch invite codes are unbound. You can generate up to 100 at a time.',
      generate: 'Generate invite code',
      created: '{count} invite code(s) generated',
      createFailed: 'Invite code operation failed',
      resent: 'A new invite code was generated',
      copied: 'Copied',
      copyCode: 'Copy code',
      copyLink: 'Copy link',
      regenerateRequired: 'Regenerate required',
      listTitle: 'Invitation records',
      selectAll: 'Select all',
      selectCode: 'Select invite code {code}',
      batchCopy: 'Copy selected codes ({count})',
      batchCopied: '{count} invite code(s) copied',
      refresh: 'Refresh',
      empty: 'No manually created invite codes yet.',
      regenerate: 'Regenerate',
      table: {
        select: 'Select',
        email: 'Player email',
        code: 'Invite code',
        status: 'Registration',
        inviteState: 'Invitation',
        expiresAt: 'Expires at',
        createdAt: 'Created at',
      },
    },
    generationRecords: {
      title: 'Generation records',
      actions: {
        refresh: 'Refresh',
        editCopy: 'Edit copy',
        backToGroups: 'Back to groups',
        viewProcess: 'Full process',
      },
      filters: {
        q: 'Search',
        qPlaceholder: 'Run, scenario, ending, or record ID',
        status: 'Status',
        allStatuses: 'All statuses',
        scenario: 'Scenario',
        allScenarios: 'All scenarios',
        search: 'Search',
      },
      stats: {
        total: 'Total',
        plays: 'Total plays',
      },
      sections: {
        videos: 'Generated videos',
      },
      status: {
        queued: 'Queued',
        running: 'Running',
        ready: 'Ready',
        failed: 'Failed',
      },
      variant: {
        endingVideo: 'Ending video',
        gameplayReplay: 'Full replay',
        gameplayTurn: 'Turn replay',
      },
      pipelineStep: {
        run: 'Run finished',
        requested: 'Video requested',
        screenplay: 'Screenplay adapted',
        production: 'Video produced',
      },
      pipeline: {
        ready: 'Ready',
        failedScreenplay: 'Screenplay failed',
        failedProduction: 'Production failed',
        producing: 'Producing video',
        awaitingScreenplay: 'Awaiting screenplay',
      },
      metrics: {
        plays: '{count} plays',
        likesAndFavorites: '{likes} likes · {favorites} saves',
      },
      group: {
        count: '{count} videos',
      },
      process: {
        title: 'Full process',
        back: 'Back',
        videoId: 'Video record ID',
        runId: 'Run ID',
        empty: 'No data',
        stages: {
          run: 'Run finished',
          requested: 'Video requested',
          screenplay: 'Screenplay',
          production: 'Scene production',
          assembly: 'Final cut',
        },
        stageNote: {
          turns: '{count} turns',
          scenes: '{count} scenes',
          segments: '{done} / {total} scenes stored',
        },
        facts: {
          status: 'Status',
          ending: 'Ending',
          player: 'Character',
          version: 'Scenario version',
          provider: 'Production method',
          generationRequest: 'Generation request',
          author: 'Author',
        },
        transcript: {
          title: 'Playthrough',
          opening: 'Opening',
          player: 'Player',
          npc: 'NPC',
          toScene: '→ Scene {scene}',
          ending: 'Ending',
        },
        scene: {
          title: 'Scenes and production',
          scene: 'Scene {scene}',
          turn: 'Turn {turn}',
          anchor: 'Anchor',
          extend: 'Extend',
          chainFrom: 'Extends the end frame of scene {scene}',
          source: 'Source turn',
          screenplay: 'Screenplay',
          production: 'Generation',
          frames: '{count} frames',
          prompt: 'Prompt',
          callPrompt: 'Prompt sent in this call',
          promptChars: '{count} chars',
          firstFrame: 'First frame',
          endFrame: 'End frame',
          calls: 'Provider calls',
          noCalls: 'No call submitted yet',
          points: '{count} points',
          stage: {
            pending: 'Not started',
            submitted: 'Generating',
            ok: 'Call finished',
            error: 'Generation failed',
            stored: 'Stored',
          },
          call: {
            submitted: 'In progress',
            ok: 'OK',
            error: 'Failed',
          },
        },
        assembly: {
          title: 'Final cut',
          opening: 'Opening CG',
          ending: 'Ending CG',
          segments: '{count} segments',
          open: 'Open final video',
        },
      },
      pagination: {
        prev: 'Previous',
        next: 'Next',
        pageSize: 'Per page',
        summary: '{total} total · page {page} of {pages}',
      },
      preview: {
        title: 'Video preview',
      },
      edit: {
        title: 'Edit video title and summary',
        videoTitle: 'Video title',
        videoSummary: 'Video summary',
        saved: 'Video title and summary updated',
      },
      empty: {
        videos: {
          title: 'No video records',
          description: 'No full-run video generation records match the current filters.',
        },
        groups: {
          title: 'No matching groups',
          description: 'No generation records match the current filters.',
        },
      },
    },
    intentVideo: {
      title: 'Intent videos',
      subtitle: 'Fallback clips recalled by player intent. Misses are queued and enter the pool once rendered offline.',
      tabs: { preparation: 'Preparation', clusters: 'Intent clusters', queue: 'Generation queue', resources: 'Resources', recall: 'Recall test', logs: 'Task logs', h3Queue: 'H3 queue' },
      h3Queue: {
        subtitle: 'H3 has one backend and runs jobs serially; the full run video and on-demand intent video paid paths both compete for priority admission through this table.',
        currentHolder: 'Current priority admission',
        noHolder: 'No paid job currently holds priority admission.',
        backend: 'H3 real queue depth',
        recent: 'Recent records',
        jobSource: 'Source',
        jobRefId: 'Job reference',
        createdAt: 'Created at',
        updatedAt: 'Updated at',
        status: 'Status',
        queued: 'Queued',
        running: 'Running',
        ready: 'Ready backends',
        source: { run_video: 'Run video', intent_video_on_demand: 'Intent video on-demand' },
      },
      preparation: {
        subtitle: 'Track action intent clips and silent player/NPC talking clips for every game.',
        scenario: 'Game',
        action: 'Action intent clips',
        player: 'Player silent clips',
        npc: 'NPC silent clips',
        clusterStatus: {
          ready: 'Analysis current',
          stale: 'Analysis outdated',
          not_analyzed: 'Not analyzed',
          no_history: 'No action samples',
        },
        actionCounts: '{turns} turns · {clusters} clusters · target {target} · playable {videos}',
        coverage: 'Historical recall coverage {rate}%',
        talkingCounts: '{entities} actors · {specs} specs · approved {approved_specs}/{specs} · {state_frames} state frames · {videos} clips',
        notConfigured: 'Not configured',
        inProgress: 'In preparation',
        ready: 'Ready',
      },
      status: { in_review: 'In review', published: 'Published', rejected: 'Disabled' },
      queueStatus: {
        pending: 'Awaiting prompt',
        prompting: 'Writing prompt',
        prompt_ready: 'Awaiting render',
        rendering: 'Rendering',
        done: 'Done',
        failed: 'Failed',
      },
      source: { cluster: 'Cluster', gameplay: 'Gameplay', admin: 'Added by operator' },
      stats: {
        total: 'Resources',
        withVideo: 'With video',
        promptOnly: 'Prompt only',
        disabled: 'Disabled',
        coverage: 'Historical coverage',
        coverageNote: '{covered}/{total} historical turns have a clip that recalls ({inputs}/{uniqueInputs} intents). Queried live.',
      },
      filters: {
        search: 'Search',
        apply: 'Filter',
        allStatuses: 'All statuses',
        allSources: 'All sources',
        allVideo: 'All',
        withVideo: 'With video',
        promptOnly: 'Prompt only',
        sort: 'Sort',
        order: 'Order',
        desc: 'Descending',
        asc: 'Ascending',
        queueSearchPlaceholder: 'Intent text',
        resourceSearchPlaceholder: 'Intent text or resource ID',
      },
      table: {
        status: 'Status',
        intent: 'Intent',
        occurrences: 'Occurrences',
        source: 'Source',
        attempt: 'Attempts',
        lastError: 'Last error',
        updatedAt: 'Updated',
        createdAt: 'Created',
        video: 'Video',
        duration: 'Duration',
        resolution: 'Resolution',
        hits: 'Hits',
        similarity: 'Similarity',
      },
      pager: {
        prev: 'Previous',
        next: 'Next',
        pageSize: 'Per page',
        loadedAll: '{n} total',
        loadedMore: '{n} on this page, more available',
      },
      queue: {
        retried: 'Requeued',
        autoGenerate: 'Auto-generate',
        autoGenerated: 'Auto-generation started — check back here for progress',
        rePrompt: 'Rewrite prompt',
        rePromptHint: 'Unbind the current resource and send it back to the front of the queue to be rewritten against the current references and spec. To shoot again with the existing prompt, use Reshoot inside the detail view.',
        rePrompted: 'Back at the front of the queue, awaiting a rewrite',
        emptyTitle: 'Queue is empty',
        emptyDescription: 'Run cli.mjs seed to load clustered intents, or wait for gameplay misses to enqueue.',
      },
      resources: {
        emptyTitle: 'No matching resources',
        emptyDescription: 'Clear or adjust the current filters.',
      },
      taskLogs: {
        emptyTitle: 'Queue is empty',
        emptyDescription: 'Run cli.mjs seed to load clustered intents, or wait for gameplay misses to enqueue.',
        pickTask: 'Pick a task on the left',
        noLogs: 'No calls logged for this task yet',
        noLogsDescription: 'It has not gone through Auto-generate or Reshoot, or it predates call logging.',
        pickLog: 'Pick a log entry in the middle',
        logCount: '{n} log entries',
        counts: '{logs} logs · {turns} source turns',
        type: {
          lemonboss: 'Write prompt',
          embedding: 'Intent embedding',
          h3: 'H3 render',
        },
        status: {
          pending: 'In progress',
          submitting: 'In progress',
          submitted: 'In progress',
          ok: 'Succeeded',
          error: 'Failed',
        },
        request: 'Request',
        response: 'Response',
        extra: 'Other',
        id: 'ID',
        providerTaskId: 'Provider task ID',
        pollCount: 'Poll count',
        fullPrompt: 'Full prompt (task + attachments concatenated)',
        contextFiles: 'Attached context files',
        contextFileNoSnapshot: 'Legacy record, no full-text snapshot was stored',
      },
      taskSources: {
        title: 'Source turns ({n})',
        open: 'View source turns',
        search: 'Search player input, turn ID, or run ID',
        apply: 'Search',
        newest: 'Newest first',
        oldest: 'Oldest first',
        empty: 'No linked source turns',
        turn: 'Turn {n}',
        playerInput: 'Original player input',
        noConversation: 'No Agent conversation for this turn',
        openConversation: 'View {role} conversation',
        page: 'Page {page} of {pages}',
      },
      recall: {
        placeholder: 'Type a player intent to test recall…',
        run: 'Recall',
        running: 'Embedding and searching…',
        hint: 'Same path the player takes: text to embedding to vector search, ranked by similarity. Below 0.75 nothing plays.',
        query: 'Query',
        hits: '{n} hits',
        emptyTitle: 'Enter a player intent',
        emptyDescription: 'Simulates the real recall path and shows which clips match and how closely.',
        noHitTitle: 'No results',
        noHitDescription: 'No intent video is similar to this text.',
      },
      spec: {
        title: 'Shot spec',
        titleHint: 'Defines the next shot; it does not alter the record of the current clip',
        empty: 'No prompt written yet.',
        write: 'Write prompt',
        references: 'Reference images',
        referencesHint: 'The order is the <Picture N> numbering: <Picture 2> in the prompt means the second image below.',
        param: {
          resolution: 'Resolution',
          width: 'Width',
          height: 'Height',
          frames: 'Duration',
          steps: 'Steps',
          seed: 'Seed',
          ref_image_short_edge: 'Reference short edge (optional)',
          turbo: 'Turbo',
        },
        turboOn: 'On (4-step distilled)',
        turboOff: 'Off (full sampling)',
        protocolRules: 'Protocol rules: with turbo on, steps must be 8 or fewer; frames must be 17n+5 at 24fps. H3 rejects anything else.',
        rationale: 'Why this tier',
        rationaleHint: 'Why this tier is worth it. This sentence is the defense for an expensive clip.',
        measuredCost: 'Measured tiers',
        edit: 'Edit spec',
        save: 'Save spec',
        saved: 'Spec saved. Press Reshoot to apply it.',
        render: 'Reshoot',
        renderHint: 'Calls H3 with the spec above. Measured at 152 to 657 seconds; this page refreshes the status itself.',
        renderStarted: 'Submitted to H3, rendering',
        renderingNote: 'Rendering. Status refreshes every 5 seconds. You can close this window and come back.',
        renders: 'Render history',
        renderedBy: 'started by {by}',
        manualNote: 'This resource has been replaced manually {n} time(s), most recently {at}. What plays now is the uploaded file, not the last entry in the render history.',
      },
      detail: {
        title: 'Intent video',
        back: 'Back',
        occurrenceMeta: '{n} occurrences · from {source} · attempt {attempt}',
        hitMeta: 'played {n} times',
        previewTask: 'Preview task',
        previewTaskHint: 'Reference images available for this request if generation runs now — the AI picks a subset and order from these, not necessarily all of them.',
        previewTaskRaw: 'View the full task text sent to Lemonboss',
        match: 'Intent match',
        intent: 'Intent text',
        intentHint: 'Saving recomputes the embedding and syncs the vector index, taking effect immediately.',
        intentSaved: 'Intent text updated and vector index synced',
        editIntent: 'Edit intent',
        rejectionReason: 'Disable reason',
        input: 'Input',
        output: 'Output',
        vector: '2048-dim vector',
        prompt: 'H3 prompt',
        origin: 'Origin',
        videoUrl: 'Video URL',
        noVideo: 'Not rendered yet',
        hasAudio: 'Has audio',
        noAudio: 'No audio',
        disable: 'Disable',
        enable: 'Restore',
        disabled: 'Removed from the recall pool',
        enabled: 'Restored to the recall pool',
        confirmDisable: 'Disabling removes this clip from the recall pool immediately. The file is kept and can be restored.',
        replace: 'Replace video',
        replacePick: 'Choose mp4',
        replaceHint: 'First and last frames are extracted server-side from this clip. mp4 only, 25MB max.',
        replaceFile: 'Ready to upload',
        replaceNote: 'Reason (optional)',
        replaceNotePlaceholder: 'Why the previous clip was replaced',
        replaceSubmit: 'Upload and replace',
        replaced: 'Video replaced, vector index synced',
        replaceWrongType: 'mp4 only, got {mime}',
        replaceTooLarge: 'File {size} exceeds the 25MB limit',
      },
      clusters: {
        totalTurns: 'Historical turns',
        uniqueInputs: 'Unique inputs',
        totalClusters: 'Intent clusters',
        needFor70: 'Needed for 70%',
        covered: 'Covered',
        queued: 'Queued',
        variants: 'Variants',
        variantCount: '{n}',
        productionColumn: 'Production',
        productionAll: 'Any production status',
        production: {
          none: 'Not queued',
          active: 'In queue',
          done: 'Shot',
          failed: 'Failed',
        },
        queueUnmatched: '{n} more queue entries match no cluster representative (usually a phrasing variant) and are not listed below. See the Generation queue tab.',
        staleWithLastSuccess: 'Data may be stale: the scheduled clustering job has failed {n} times in a row. Last success was {at}.',
        staleNeverSucceeded: 'Data may be stale: the scheduled clustering job has failed {n} times in a row and has never succeeded.',
        coverageColumn: 'Recall coverage',
        coverageAll: 'Any coverage status',
        coverage: {
          covered: 'Covered',
          uncovered: 'Uncovered',
        },
        coverageTarget: 'Coverage target',
        coverageOff: 'Any rank',
        coverageNeeds: '{percent}% needs the first {rank} ({rate}% actual)',
        minOccurrences: 'Minimum occurrences',
        minOccurrencesOff: 'Any frequency',
        minOccurrencesValue: '{n} or more',
        searchPlaceholder: 'Search the representative intent or its variants',
        pageSummary: '{n} clusters · page {page} of {pages}',
        note: 'Coverage reflects the live recall pool: recorded when a player types it, re-evaluated when a new clip ships.',
        noClusterFile: 'No action-intent clustering analysis exists for this scenario. Run analyze-intent-clusters.mjs first.',
        enqueue: 'Add to queue',
        enqueued: 'Added to the generation queue',
        merged: 'An identical or similar intent was already queued; merged',
        enqueueAuto: 'Add to auto queue',
        autoQueued: 'Added to the auto queue — claim, prompt writing, and rendering will all run automatically; watch the production column for progress',
        selectAll: 'Select all',
        selectRow: 'Select "{intent}"',
        batchAutoQueue: 'Batch add to auto queue ({n})',
        batchProgress: '{done}/{total} done',
        batchAutoQueuedAllOk: 'Batch auto queue complete: all {n} succeeded',
        batchAutoQueuedPartial: 'Batch auto queue complete: {ok} succeeded, {fail} failed',
        detailTitle: 'Cluster detail',
        back: 'Back',
        notFound: 'This cluster was not found — the current analysis may have changed.',
        representative: 'Representative',
        occurrenceDetail: '{n} turns across {v} phrasings',
        samples: 'Phrasing variants',
        samplesTruncated: '{n} more not captured in the list',
        emptyTitle: 'No matching clusters',
        emptyDescription: 'Clear or adjust the current filters.',
      },
    },
    assetLibrary: {
      title: 'Asset library',
      subtitle: 'Character art, backgrounds, NPC art, prop art, and shoot prompts, one list per game; also browse every generated media file.',
      create: 'New asset',
      deleted: 'Deleted',
      deleteConfirm: 'Delete asset "{name}"?',
      tabs: {
        talkingVideos: 'Silent dialogue videos',
        curated: 'Curated library',
        allMedia: 'All media',
      },
      talkingVideos: {
        specReviewTitle: 'Actor states and H3 generation specs',
        specReviewDescription: 'Review each actor-specific finite speaking state, selection conditions, full prompt, generation parameters, and references before generating and binding a silent video.',
        noSpecs: 'No dialogue performance specs are ready for review',
        stateCount: '{count} states',
        identityReference: 'Actor identity reference',
        identityReferenceDescription: 'The single identity, age, and costume reference shared by every state frame for this actor. Open it to inspect the source asset.',
        promptQuality: {
          draft: 'Pending review',
          approved: 'Approved',
          rejected: 'Rejected',
        },
        promptQualityLabel: 'Spec status',
        allPromptQualities: 'All spec statuses',
        availablePhases: 'Available story phases',
        videoReady: 'Video ready',
        videoPending: 'Awaiting generation',
        videoPreviewEmpty: 'Video not generated yet',
        bindVideo: 'Bind video assets',
        reviewPrompt: 'Review spec',
        approvePrompt: 'Approve',
        rejectPrompt: 'Reject',
        promptReviewed: 'Prompt review status updated',
        generatedTitle: 'Generated and bound videos',
        generatedDescription: 'Manage generated silent videos and their boundary frames here. Runtime playback only uses complete active triplets.',
        generatedVideoPreview: 'Generated video preview',
        videoPendingHint: 'No silent video has been generated and bound for this state yet. Approve the spec, then bind one below or wait for automatic generation.',
        selectionLogic: 'State selection conditions',
        composition: 'Composition and position',
        facialPerformance: 'Expression and facial detail',
        mouthMotion: 'Mouth movement cadence',
        microAction: 'State-specific motion',
        excludedEmotions: 'Excluded emotions',
        stateFrame: 'State reference frame',
        stateFrameAndPrompt: 'State reference frame and generation prompt',
        openStateFrame: 'Open state reference asset',
        h3Params: 'H3 generation parameters',
        referenceImages: 'Scene and actor references',
        fullPrompt: 'Full H3 prompt',
        embeddingCall: 'Open prompt embedding call',
        emptyTitle: 'No silent dialogue videos configured',
        emptyDescription: 'Add a configuration to bind an actor, expression, silent video, and boundary frames.',
        create: 'Add configuration',
        createTitle: 'Add silent dialogue video',
        editTitle: 'Edit silent dialogue video',
        actor: 'Actor',
        actorType: 'Actor type',
        allActors: 'All actors',
        pack: 'Game',
        allPacks: 'All games',
        scope: 'Game and expression',
        expression: 'Expression',
        expressionHint: 'Use lowercase letters, digits, and underscores. Missing exact states fall back to default.',
        video: 'Silent dialogue video',
        startFrame: 'Start frame',
        endFrame: 'End frame',
        frames: 'Boundary frames',
        videoAssetId: 'Video asset ID',
        startFrameAssetId: 'Start frame asset ID',
        endFrameAssetId: 'End frame asset ID',
        silentHint: 'Video metadata is checked on save. Videos with an audio track cannot be bound.',
        silent: 'Silent',
        hasAudio: 'Contains audio',
        openAsset: 'Open asset details',
        searchPlaceholder: 'Search actor, actor ID, or expression',
        status: 'Status',
        allStatuses: 'All statuses',
        published: 'Active',
        withdrawn: 'Withdrawn',
        sort: 'Sort by',
        order: 'Order',
        sortUpdated: 'Updated time',
        sortActor: 'Actor name',
        sortPack: 'Game name',
        desc: 'Descending',
        asc: 'Ascending',
        history: 'History',
        historyTitle: 'Silent dialogue video history',
        restore: 'Restore',
        withdraw: 'Withdraw',
        withdrawConfirm: 'Withdraw the {expression} silent dialogue video for "{name}"? All three bindings will be withdrawn together.',
        withdrawnNotice: 'All three bindings were withdrawn',
        saved: 'All three bindings were saved',
        kind: {
          video: 'Video',
          start_frame: 'Start frame',
          end_frame: 'End frame',
          state_frame: 'State reference frame',
        },
        action: {
          create: 'Created',
          update: 'Updated',
          status_change: 'Status changed',
        },
      },
      curated: {
        pack: 'Game',
      },
      allMedia: {
        pack: 'Game',
        allPacks: 'All games',
        global: 'Global',
        preview: 'Preview',
        size: 'Size',
        searchPlaceholder: 'Search by tag',
        open: 'Open',
        id: 'ID',
        draftId: 'Draft ID',
        mime: 'MIME',
        objectKey: 'Object key',
        publicUrl: 'URL',
        hash: 'Hash',
        metadata: 'Metadata',
        createdAt: 'Created at',
        updatedAt: 'Updated at',
      },
      detail: {
        title: 'Asset detail',
        back: 'Back',
        notFound: 'Asset not found.',
        basicInfo: 'Basic info',
        storageInfo: 'Storage info',
      },
      filters: {
        allTypes: 'All types',
        search: 'Search',
        searchPlaceholder: 'Search by name / tag / description',
        apply: 'Filter',
      },
      type: {
        image: 'Image',
        video: 'Video',
        audio: 'Audio',
        prompt: 'Prompt',
      },
      table: {
        type: 'Type',
        kind: 'Kind',
        subject: 'Subject',
        stateLabel: 'State',
        tag: 'Tag',
        name: 'Name',
        description: 'Description',
        content: 'Content',
        updatedAt: 'Updated at',
      },
      kind: {
        scene: 'Scene anchor',
        character_identity: 'Character default identity',
        character_state: 'Character state variant',
        prop: 'Prop',
      },
      pager: {
        summary: '{total} total · page {page} / {pages}',
        prev: 'Previous',
        next: 'Next',
        pageSize: 'Per page',
      },
      form: {
        createTitle: 'New asset',
        editTitle: 'Edit asset',
        tagHint: 'Unique within this game. Cannot be changed after creation — delete and recreate to rename it.',
        kindHint: 'Intent video prompt-writing uses this field to tell what this item is for.',
        subjectHint: 'e.g. npc, player — groups a character\'s default identity with its state variants.',
        stateLabelHint: 'What this state is (e.g. "in the bath") — intent video lists this for the AI to see.',
        textContent: 'Prompt text',
        file: 'File',
        fileReplaceHint: 'Leave empty to keep the current file, or pick one to replace it.',
        existingMediaAssetId: 'Existing media asset id (optional)',
        existingMediaAssetIdHint: 'Already uploaded under "All media"? Paste its id here instead of uploading the file again. Fill this and skip the file picker.',
        saved: 'Saved',
      },
      emptyTitle: 'No assets in this game yet',
      emptyDescription: 'Click "New asset" in the top right to add the first one.',
    },
    promptLibrary: {
      emptyTitle: 'No matching prompts',
      approve: 'Approve',
      reject: 'Reject',
      reviewed: 'Review status updated',
      quality: { draft: 'Draft', approved: 'Approved', rejected: 'Rejected' },
      filters: {
        allModalities: 'All modalities',
        allQualities: 'All review statuses',
        packPlaceholder: 'Filter by pack_id (leave empty for all)',
      },
      table: {
        quality: 'Quality',
        modality: 'Modality',
        pack: 'Pack',
        promptText: 'Prompt text',
        source: 'Source',
        createdAt: 'Created at',
        similarity: 'Similarity',
      },
      recall: {
        title: 'Recall test',
        hint: 'Before writing a new prompt, search for similar approved prompts to use as few-shot references.',
        placeholder: 'Enter an intent/effect description to find similar produced prompts',
        run: 'Search',
        noHitTitle: 'No similar prompts found',
      },
    },
    entityLibrary: {
      create: 'New entity',
      created: 'Created',
      emptyTitle: 'No entities yet',
      type: { npc: 'NPC', player: 'Player character', scene: 'Scene', prop: 'Prop', effect: 'Effect' },
      filters: {
        allTypes: 'All types',
        searchPlaceholder: 'Search by name',
      },
      table: {
        image: 'Image',
        type: 'Type',
        canonicalName: 'Name',
        aliases: 'Aliases',
        status: 'Status',
        source: 'Source',
      },
      form: {
        aliasesHint: 'Separate multiple aliases with commas',
      },
      detail: {
        factsTitle: 'World-building hard facts',
        factsHint: 'World-level settings a pack cannot override, stored as free-form JSON until the lore bible defines a fixed schema.',
        factsInvalidJson: 'Not a valid JSON object',
        factsSaved: 'World-building facts saved',
        deepResearch: {
          run: 'Run deep research',
          hint: 'Finds reference images for NPC/player/scene/prop, or prompt writeups for effect. Auto-ingested items are usable immediately; withdraw unsuitable ones later from the asset list.',
          done: 'Ingested {ingested}, skipped {skipped}',
        },
        assets: {
          title: 'Attached assets',
          emptyTitle: 'No assets attached yet',
          type: 'Asset type',
          pack: 'Pack',
          role: 'Role',
          status: 'Status',
          ref: 'Reference',
          canonical: 'Canonical (all packs)',
          withdraw: 'Withdraw',
          withdrawn: 'Withdrawn',
          saved: 'Saved',
          promptId: 'prompt_id',
          mediaAssetId: 'media_asset_id',
        },
        relationships: {
          title: 'Relationships',
          emptyTitle: 'No relationships recorded yet',
          direction: 'Direction',
          other: 'Other entity',
          address: 'Address',
          notes: 'Notes',
          toEntityId: 'Target entity id',
          saved: 'Saved',
        },
      },
    },
    analytics: {
      title: 'Operations overview', since: 'Start date', until: 'End date', refresh: 'Refresh report',
      metrics: {
        active_players: 'Active players', interactions: 'Interactions', say_turns: 'Dialogues', act_turns: 'Actions',
        credits_spent: 'Credits spent', credits_granted: 'Credits granted', runs_started: 'Runs started', runs_completed: 'Levels completed',
        seasons_started: 'Seasons started', seasons_completed: 'Seasons completed', paying_players: 'Paying players', activePlayers: 'Active players',
      },
      daily: { title: 'Daily interaction trend', date: 'Date' },
      drilldown: {
        title: '{metric} details', summary: '{total} players total; showing the top 50', backToTrend: 'Back to trend',
        player: 'Player', accountType: 'Account type', lastInteraction: 'Last interaction',
        accountTypes: { registered: 'Registered', guest: 'Guest' },
      },
      interactions: {
        title: '{player} interaction details', summary: '{total} interactions total; showing the latest 50', back: 'Back to player details',
        time: 'Interaction time', story: 'Series / level', position: 'Story position', mode: 'Type', content: 'What the player said / did',
        season: 'Season {number}', episode: 'Episode {number}', turn: 'Turn {number}', modes: { say: 'Dialogue', act: 'Action' },
      },
    },
    contentAnalytics: {
      title: 'Content interaction report', series: 'Series', scenario: 'Level', episode: 'Episode', standalone: 'Standalone',
      activePlayers: 'Active players', started: 'Runs started', completed: 'Levels completed', completionRate: 'Completion rate',
      interactions: 'Interactions', dialogues: 'Dialogues', actions: 'Actions',
    },
    ga4Behavior: {
      title: 'GA4 user behavior', description: 'Aggregated sources, countries and regions, pages, devices, videos, clicks, scene misses, and input methods.',
      setupTitle: 'GA4 Data API is not configured', setupBody: 'Configure the numeric Property ID and Google service-account credentials on visual-game-server, then grant that service account Viewer access to the GA4 property.',
      flowHint: 'Sources use page_referrer. This is useful for page-to-page navigation but is not a full GA4 path exploration.',
      sceneHint: 'Only scene clicks that missed an interactive target are shown. grid_x / grid_y divide the frame into ten columns and rows.',
      videoHint: 'Viewing progress is recorded once at 25%, 50%, and 75%. Counts accumulate when the same video is watched multiple times.',
      videoOpen: 'Open video', videoUnavailable: 'Video unavailable',
      videoStartNone: 'No recorded starts', videoStartCount: 'Started {count} times',
      videoProgressNone: 'No viewing checkpoint reached', videoProgressCount: 'Reached {count} viewing checkpoints',
      videoCompletionNone: 'No recorded completions', videoCompletionCount: 'Completed {count} times', videoUserCount: '{count} users',
      summary: { unique_visitors: 'UV (unique visitors)', active_users: 'Active users', page_views: 'PV (page views)', sessions: 'Sessions', video_viewers: 'Video viewers', video_starts: 'Video starts', video_completions: 'Video completions', ui_clicks: 'UI clicks', scene_clicks: 'Scene clicks', typed_inputs: 'Typed inputs', selected_inputs: 'Selected inputs' },
      sections: { traffic: 'UV / PV trend', sources: 'Media sources', locations: 'Countries / regions', pages: 'Pages viewed', flows: 'Page navigation', devices: 'Devices and browsers', videos: 'Video viewing', clicks: 'UI clicks', sceneMisses: 'Scene miss heatmap', inputs: 'Typed or selected' },
      sourceNotes: { x_referral: 'The user arrived through a link on X (formerly Twitter)', facebook_referral: 'The user arrived through a link on Facebook', instagram_referral: 'The user arrived through a link on Instagram', youtube_referral: 'The user arrived through a link on YouTube', tiktok_referral: 'The user arrived through a link on TikTok', direct: 'GA4 received no referrer; the user may have entered the URL, used a bookmark, or lost referrer data during redirects', unassigned: 'GA4 could not determine a default channel for these sessions because attribution data is missing, unusual, or not yet processed', organic_social: 'Unpaid traffic from a social media link; see Source / medium for the platform', organic_search: 'Unpaid traffic from a search engine such as Google', referral: 'Traffic from a link on another website', paid_social: 'Traffic from a social media advertisement', paid_search: 'Traffic from a search advertisement', other: 'GA4 default channel classification; see Source / medium for details' },
      clickNote: 'The user clicked “{action}” in “{area}”',
      clickLabels: {
        elements: { continue_game: 'Continue game', start_game: 'Start game', view_journey: 'View story journey', watch_drama: 'Watch drama', select_character: 'Select character', continue_ending: 'Continue ending', enter_next_episode: 'Enter next episode', view_ending_record: 'View ending record', select_video_episode: 'Select video episode', share_video: 'Share video', toggle_video_favorite: 'Favorite or unfavorite video', toggle_video_like: 'Like or unlike video', video_to_game: 'Enter game from video' },
        surfaces: { landing_hero: 'Homepage hero', landing_story: 'Homepage story carousel', character_select: 'Character selection', ending_book: 'Ending page', game_hud: 'Game controls', video_center: 'Video center' },
      },
      inputLabels: { methods: { typed: 'Typed input', voice: 'Voice input', suggested_choice: 'Suggested choice', map_move: 'Map movement' }, modes: { say: 'Dialogue', act: 'Action' } },
      videoLabels: { placements: { video_center: 'Video center' } },
      columns: { date: 'Date / time', unique_visitors: 'UV (unique visitors)', page_views: 'PV (page views)', channel: 'Default channel', source_medium: 'Source / medium', campaign: 'Campaign', note: 'Note', country: 'Country', region: 'Region', path: 'Page path', title: 'Page title', views: 'Views', users: 'Users', from: 'Source page', to: 'Destination page', category: 'Device', operating_system: 'Operating system', browser: 'Browser', sessions: 'Sessions', video_id: 'Video ID', video_title: 'Video title', video_url: 'Video link', placement: 'Viewing location', starts: 'Started', progress_events: 'Viewing progress', completions: 'Completed', element_id: 'Element ID', surface: 'Surface', action: 'Clicked item', area: 'Page area', clicks: 'Clicks', scene_id: 'Scene ID', grid_x: 'Grid X', grid_y: 'Grid Y', pointer_type: 'Pointer', input_method: 'Input method', mode: 'Mode', submissions: 'Submissions' },
    },
    users: {
      title: 'C-end users',
      provision: {
        trigger: 'Provision account', title: 'Admin account provisioning', email: 'Email', password: 'Initial password', passwordConfirmation: 'Confirm initial password', locale: 'Account locale', initialCredits: 'Initial credits', reason: 'Provisioning reason', confirm: 'Provision account', success: 'Account provisioned',
        notice: 'Admin only: the email will be marked verified, the password will be stored using the production hash, and the initial credits and reason will be recorded in the audit log and credit ledger.',
      },
      metrics: {
        total: 'Total users',
        registered: 'Registered',
        guests: 'Guests',
        players: 'Played',
        payers: 'Paid',
      },
      filters: {
        q: 'User search',
        qPlaceholder: 'Email, display name, or user ID',
        type: 'Account type',
        subscription: 'Subscription',
        since: 'Period start',
        until: 'Period end',
        all: 'All',
        search: 'Search',
      },
      type: { registered: 'Registered', guest: 'Guest' },
      subscriptionStatus: {
        none: 'No subscription',
        active: 'Active',
        expired: 'Expired',
        canceled: 'Canceled',
      },
      table: {
        identity: 'Identity',
        type: 'Type',
        displayName: 'Display name',
        locale: 'Locale',
        credits: 'Credits',
        creditsSpent: 'Spent in period',
        creditsGranted: 'Granted in period',
        subscription: 'Subscription',
        runs: 'Runs',
        turns: 'Lifetime turns',
        interactions: 'Period interactions',
        sayTurns: 'Dialogues in period',
        actTurns: 'Actions in period',
        completedRuns: 'Completed levels',
        completedSeasons: 'Completed seasons',
        endings: 'Endings',
        orders: 'Orders',
        lastActive: 'Last active',
        createdAt: 'Created',
      },
      pagination: {
        summary: '{total} users · page {page} of {pages}',
        pageSize: 'Rows per page',
        previous: 'Previous',
        next: 'Next',
      },
      empty: {
        title: 'No users found',
        description: 'Try clearing or changing the current filters.',
      },
      detail: {
        title: 'User detail',
        back: 'Back to users',
        refresh: 'Refresh',
        profile: 'Account profile',
        overview: 'Subscription and play overview',
        fields: {
          id: 'User ID',
          displayName: 'Display name',
          nickname: 'Nickname',
          avatar: 'Avatar URL',
          accountLocale: 'Account locale',
          profileLocale: 'Profile locale',
          emailVerified: 'Email verified',
          createdAt: 'Created',
        },
        metrics: {
          credits: 'Credits',
          runs: 'Runs',
          activeRuns: 'Active runs',
          completedRuns: 'Completed runs',
          turns: 'Turns',
          sayTurns: 'Dialogues',
          actTurns: 'Actions',
          creditsSpent: 'Credits spent',
          creditsGranted: 'Credits granted',
          completedSeasons: 'Completed seasons',
          scenarios: 'Scenarios',
          progress: 'Progress records',
          endings: 'Endings',
          saves: 'Save slots',
          lastActive: 'Last active',
        },
        subscription: {
          status: 'Status',
          level: 'Level',
          expiresAt: 'Expires',
          provider: 'Provider',
          source: 'Source',
        },
        guestDevices: {
          title: 'Guest devices',
          status: 'Status',
          lastSeen: 'Last seen',
          mergedUser: 'Merged user',
          statuses: { active: 'Active', merged: 'Merged' },
        },
        behavior: {
          title: 'User behavior timeline (latest 200)', empty: 'No behavior events yet.', sessions: 'Sessions', events: 'Events', engagement: 'Engaged time',
          time: 'Time', event: 'User action', page: 'Page', duration: 'Engaged time', details: 'What happened',
          durationValue: '{minutes}m {seconds}s',
          viewMode: 'Timeline view', pageVisits: 'Page visits', rawEvents: 'Raw events', operations: 'actions',
          grouping: { exact: 'Exact grouping', inferred: 'Legacy estimate' },
          help: 'The first line describes the action for operations. The gray second line is the technical event identifier. Engaged time only counts time while the page was active.',
          unknown: 'Unknown',
          unknownVideo: 'Unknown video', openVideo: 'Open video',
          unknownControl: 'page control', unknownArea: 'current page',
          unknownDevice: 'Unknown device', unknownLocation: 'Unknown location', unknownSource: 'Unknown source',
          dayDevice: 'Device: {value}', dayLocation: 'Location: {value}', daySource: 'Source: {value}',
          clickEventName: 'Clicked “{target}”', resultEventName: '“{action}” result',
          oauthControl: 'Continue with {method}',
          noAdditionalDetails: 'No additional details',
          eventNames: {
            page_view: 'Viewed a page', page_engagement: 'Stayed on a page', game_start: 'Started a game', turn_submit: 'Submitted a story action',
            ending_reached: 'Reached a story ending', vg_video_start: 'Started a video', vg_video_progress: 'Watched a video',
            vg_video_complete: 'Completed a video', vg_video_error: 'Video playback failed', ui_click: 'Clicked a page control',
            action_result: 'Action result',
            scene_click: 'Clicked a scene interaction', billing_paypoint_gameplay: 'Reached a gameplay paywall',
            billing_paypoint_ending_video: 'Reached an ending video paywall', billing_paypoint_story_video: 'Reached a story video paywall',
            login: 'Logged in', sign_up: 'Created an account',
            content_click: 'Clicked homepage content',
          },
          pageNames: {
            home: 'Home', stories: 'Story library', dramas: 'Drama library', search: 'Search', login: 'Login', game: 'Game',
            storyTree: 'Story tree', storyDetail: 'Story details', playerProfile: 'Player profile', account: 'Account',
            accountEdit: 'Edit profile', accountSettings: 'Account settings', myGames: 'My games', myVideos: 'My videos',
            myFavorites: 'My favorites', videoGeneration: 'Video generation', create: 'Create', billing: 'Billing',
            credit: 'Credits', waitlist: 'Waitlist',
          },
          descriptions: {
            pageView: 'Entered “{page}”', pageEngagement: 'Stayed on “{page}” for {duration}',
            gameStart: 'Started a story (story ID: {id})', gameStartGeneric: 'Started a story',
            gameStartNamed: 'Started “{title}” (story ID: {id})',
            turnSubmit: 'Submitted a story action using {method}', turnSubmitNamed: 'Submitted a story action in “{title}” using {method}',
            endingReached: 'Reached an ending (ending ID: {id})', endingReachedNamed: 'Reached an ending in “{title}” (ending ID: {id})',
            endingReachedGeneric: 'Completed the story and reached an ending', videoStart: 'Started “{video}” (ID: {id})', videoProgress: 'Watched “{video}” (ID: {id}) to {percent}%',
            videoComplete: 'Completed “{video}” (ID: {id})',
            videoError: 'Video “{video}” (ID: {id}) failed to play (error: {code})', uiClick: 'Clicked “{target}” in “{surface}”',
            actionResult: '“{action}” result: {result}',
            sceneClick: 'Clicked the scene; result: {result}', auth: 'Completed using {method}',
            contentClick: 'Clicked {type} “{title}” from {surface} (position {position}) and opened {destination}',
          },
          inputMethods: { text: 'text input', voice: 'voice input', choice: 'a choice', unknown: 'an unknown method' },
          authMethods: { email: 'email', google: 'Google', apple: 'Apple', x: 'X', discord: 'Discord', facebook: 'Facebook', tiktok: 'TikTok', guest: 'guest access', password: 'email and password' },
          interactionResults: { hit: 'interaction triggered', miss: 'no interaction triggered' },
          contentTypes: { game: 'game', drama: 'drama' },
          contentSurfaces: { homepage_carousel: 'homepage carousel', homepage_game: 'homepage games', homepage_drama: 'homepage dramas' },
          actionResults: { succeeded: 'succeeded', failed: 'failed', canceled: 'canceled' },
          deviceTypes: { desktop: 'Desktop', mobile: 'Mobile', tablet: 'Tablet' },
          trafficSources: { direct: 'Direct', unknown: 'Unknown source' },
          trafficMediums: { none: 'None', referral: 'Referral', campaign: 'Campaign', organic: 'Organic search', cpc: 'Paid ads', email: 'Email' },
          controlLabels: {
            start_game: 'Start game', select_character: 'Select character', retry_load: 'Retry', back: 'Back', back_home: 'Back to home', back_account: 'Back to account', back_to_game: 'Back to game', brand_home: 'VOKO home',
            nav_logo: 'site logo', nav_search: 'Search', nav_login: 'Log in or sign up', nav_account: 'Account', nav_signup_reward: 'sign-up reward', logout: 'Log out',
            auth_prompt_close: 'Close login dialog', auth_method_back: 'Back to login methods', auth_email: 'Email login', auth_more_options: 'More login methods', send_login_code: 'Send login code', email_code_login: 'Log in with code',
            story_card: 'Story card', game_card: 'Game card', start_checkout: 'Continue to payment', select_credit_pack: 'Select credit pack', open_billing: 'Buy credits', open_credit_history: 'Credit history', credit_tab: 'Credit tab', credit_history_filter: 'Credit history filter',
            share_story: 'Share story', share_video: 'Share video', video_share: 'Share video', toggle_story_like: 'Like story', toggle_story_favorite: 'Favorite story', toggle_video_like: 'Like video', toggle_video_favorite: 'Favorite video', video_like: 'Like video', video_favorite: 'Favorite video',
            view_journey: 'View story tree', view_player: 'View creator', watch_drama: 'Watch drama', video_to_game: 'Enter game', select_video_episode: 'Select video', open_video: 'Open video', open_video_title: 'Open video', owned_video_card: 'Open my video', favorite_game_card: 'Open favorite game',
            video_playback_toggle: 'Play or pause video', video_sound_toggle: 'Toggle sound', video_summary_toggle: 'Expand or collapse summary', play_ending_video: 'Play ending video', open_generated_video: 'Open generated video', download_replay: 'Download replay',
            clear_filters: 'Clear filters', clear_search: 'Clear search', clear_search_history: 'Clear search history', search_back_home: 'Back to home', search_history_item: 'Previous search', popular_search: 'Popular search', search_result: 'Search result', search_type: 'Search type',
            story_filter: 'Story filter', story_layout: 'Story layout', story_drama_sort: 'Drama sorting', story_tree_sort: 'Story tree sorting', player_sort: 'Video sorting', generation_filter: 'Generation filter', video_library_tab: 'Video category', account_tab: 'Account section', notes_tab: 'Notes section', notes_entry: 'Note entry',
            player_synopsis_toggle: 'Expand or collapse summary', load_more: 'Load more', season_toggle: 'Expand or collapse season', story_level: 'Story chapter', open_story_log: 'Play history', open_story_picker: 'Switch story', select_run: 'Open run', open_notes: 'Open run notes',
            view_ending_record: 'View ending record', replay_episode: 'Replay episode', continue_ending: 'Continue ending', enter_next_episode: 'Enter next episode', confirm_story_fork: 'Create story branch', confirm_delete_branch: 'Delete story branch',
            edit_profile: 'Edit profile', save_profile: 'Save profile', send_verification_code: 'Send verification code', account_games: 'My games', account_videos: 'My videos', account_favorites: 'My favorites', account_generation: 'Video generations',
            create_project: 'Create project', upload_asset: 'Upload asset', submit_creation: 'Submit creation', delete_project: 'Delete project', save_auto_reload: 'Save auto-reload', disable_auto_reload: 'Disable auto-reload',
            submit_waitlist: 'Submit waitlist application', retry_waitlist: 'Edit application', open_invite: 'Use invitation',
            oauth_login: 'Third-party login', load_search: 'Load search results', submit_search: 'Search', load_story_tree: 'Load story tree', load_standalone_notes: 'Load run notes', load_credit_history: 'Load credit history', load_video_generations: 'Load video generations', fork_story_turn: 'Create story branch', delete_story_branch: 'Delete story branch',
          },
          surfaceLabels: {
            character_select: 'character selection', game_hud: 'game controls', site_header: 'top navigation', site_header_mobile: 'mobile header', mobile_navigation: 'mobile navigation', auth_dialog: 'login dialog', auth_scrim: 'outside the login dialog',
            homepage_carousel: 'homepage carousel', homepage_game: 'homepage games', stories_header: 'story page header', stories_filters: 'story filters', stories_list: 'story list', story_detail: 'story details', story_related_dramas: 'related dramas', story_tree: 'story tree',
            dramas_list: 'drama list', video_center: 'video center', player_stories: 'player stories', owned_video_player: 'my video player', video_library: 'video library', favorites: 'favorites', player_page: 'player profile',
            search_header: 'search header', search_history: 'search history', search_popular: 'popular searches', search_results: 'search results', run_history: 'run history', standalone_notes: 'run notes', ending_book: 'ending book', ending_book_mobile: 'mobile ending book',
            account_mobile: 'mobile account', account_header: 'account header', account_sidebar: 'account sidebar', account_edit: 'edit profile', account_settings: 'account settings', my_games: 'my games', my_games_recent: 'recent games', my_games_popular: 'popular games',
            credit_page: 'credits page', credit_balance: 'credit balance', credit_products: 'credit products', credit_history: 'credit history', credit_auto_reload: 'auto-reload settings', billing_auto_reload: 'auto-reload settings', creator_projects: 'creator projects', creator_projects_mobile: 'mobile creator projects', creator_desktop: 'creation form', creator_mobile: 'mobile creation form', video_generation: 'video generations', waitlist: 'waitlist',
          },
        },
        ledger: {
          title: 'Credit ledger (latest 50)',
          empty: 'No credit ledger entries.',
          time: 'Time',
          id: 'Ledger ID',
          type: 'Type',
          amount: 'Amount',
          balance: 'Balance after',
          reason: 'Reason',
          reference: 'Reference',
          types: { grant: 'Grant', deduct: 'Deduct' },
        },
        audit: {
          title: 'Admin actions (latest 50)',
          empty: 'No admin actions.',
          time: 'Time',
          action: 'Action',
          operator: 'Operator',
          reason: 'Reason',
          actions: {
            account_provisioned: 'Account provisioned',
            profile_updated: 'Profile updated',
            credits_granted: 'Credits granted',
            credits_deducted: 'Credits deducted',
            subscription_updated: 'Subscription updated',
          },
        },
        orders: {
          title: 'Orders (latest 30)',
          empty: 'No orders.',
          time: 'Time',
          id: 'Order ID',
          product: 'Product',
          mode: 'Mode',
          provider: 'Provider',
          status: 'Status',
          amount: 'Amount',
          refund: 'Refund',
          modes: { payment: 'One-time', subscription: 'Subscription' },
          statuses: {
            pending: 'Pending',
            paid: 'Paid',
            refunded: 'Refunded',
            disputed: 'Disputed',
          },
        },
        runs: {
          title: 'Recent runs (latest 20)',
          empty: 'No game runs.',
          time: 'Last updated',
          id: 'Run ID',
          scenario: 'Scenario',
          status: 'Status',
          turns: 'Turns',
          phase: 'Phase',
          location: 'Location',
          viewInteractions: 'View interactions',
          interactions: {
            title: '{scenario} · Interactions',
            summary: '{total} interactions in this run; showing the latest 100 in reverse chronological order.',
            empty: 'This run has no completed interactions.',
            turn: 'Turn',
          },
          statuses: { active: 'Active', completed: 'Completed' },
        },
      },
      actions: {
        reason: 'Reason',
        profile: {
          trigger: 'Edit profile',
          title: 'Edit user profile',
          nickname: 'Nickname',
          avatar: 'Avatar URL',
          locale: 'Locale',
          success: 'User profile updated.',
        },
        credits: {
          trigger: 'Adjust credits',
          title: 'Adjust user credits',
          type: 'Adjustment',
          grant: 'Grant',
          deduct: 'Deduct',
          amount: 'Credits',
          notice: 'Current balance: {balance}. A deduction with insufficient balance fails without writing a ledger entry.',
          confirm: 'Confirm adjustment',
          success: 'Credits adjusted and recorded in the ledger.',
        },
        subscription: {
          trigger: 'Edit subscription',
          title: 'Edit user subscription',
          level: 'Subscription level',
          status: 'Status',
          expiresAt: 'Expires at',
          success: 'Subscription updated.',
          rules: {
            none: 'No subscription requires the free level and no expiry.',
            active: 'Active requires a non-free level. Leave expiry empty for no fixed expiry.',
            expired: 'Expired requires a non-free level and an expiry no later than now.',
            canceled: 'Canceled requires a non-free level. This does not alter or create payment orders.',
          },
        },
      },
    },
    waitlist: {
      title: 'Early access waiting list',
      pagination: {
        previous: 'Previous',
        next: 'Next',
      },
      metrics: {
        total: 'Total',
        joined: 'Joined',
        invitePending: 'Invite pending',
        invited: 'Invited',
        registered: 'Registered',
        rejected: 'Rejected',
        failedDeliveries: 'Failed deliveries',
      },
      filters: {
        q: 'Search email or name',
        search: 'Search',
        status: 'Status',
        source: 'Source',
        useCase: 'Use case',
        inviteState: 'Invite state',
        createdFrom: 'Created from',
        createdTo: 'Created to',
        all: 'All',
      },
      table: {
        email: 'Email',
        displayName: 'Name',
        status: 'Status',
        inviteState: 'Invite',
        source: 'Source',
        useCase: 'Use case',
        createdAt: 'Created',
        updatedAt: 'Updated',
        lastEvent: 'Last event',
      },
      detail: {
        profile: 'Profile',
        consent: 'Consent',
        status: 'Status',
        delivery: 'Deliveries',
        timeline: 'Timeline',
        notes: 'Notes',
        actions: 'Actions',
        currentNote: 'Current note',
        invariantError: 'Consent evidence is missing. Invite actions are blocked.',
      },
      fields: {
        displayName: 'Display name',
        locale: 'Locale',
        source: 'Source',
        useCase: 'Use case',
        note: 'Note',
        consentAcceptedAt: 'Marketing consent accepted at',
        consentRevokedAt: 'Marketing consent revoked at',
        expiresAt: 'Expires at',
        registeredUserId: 'Registered user id',
        registeredAt: 'Registered at',
        rejectedReason: 'Rejected reason',
        provider: 'Provider',
        messageId: 'Message id',
        sentAt: 'Sent at',
        failedAt: 'Failed at',
        errorMessage: 'Error message',
      },
      status: {
        joined: 'Joined',
        invite_pending: 'Invite pending',
        invited: 'Invited',
        registered: 'Registered',
        rejected: 'Rejected',
      },
      source: {
        landing_hero: 'Homepage hero',
        landing_footer: 'Homepage footer',
        pricing: 'Pricing page',
        manual_admin: 'Added manually by admin',
      },
      invite: {
        none: 'Pending approval',
        pending: 'Pending',
        sent: 'Sent',
        failed: 'Failed',
        expired: 'Expired',
        claimed: 'Claimed',
        closed: 'Closed',
      },
      modal: {
        manualTitle: 'Create manual entry',
        inviteTitle: 'Invite applicant',
        resendTitle: 'Resend invite',
        noteTitle: 'Add note',
        rejectTitle: 'Reject applicant',
        exportTitle: 'Export entries',
        operatorNote: 'Operator note',
        rejectReason: 'Reject reason',
        exportSummary: 'Export uses the active filter set.',
      },
      toast: {
        loaded: 'Waiting list loaded.',
        saved: 'Action completed.',
        exported: 'CSV exported.',
        failed: 'Action failed.',
      },
    },
  },
}
